import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import BAULT_ABI from 'abis/Bault.json'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { useMemo } from 'react'
import type { Abi } from 'viem'
import { formatUnits, parseUnits } from 'viem'
import { Address } from 'viem'

export function usePreviewBaultShares(
  baultAddress: Address | undefined,
  typedValue: CurrencyAmount<Token> | undefined,
  shouldFetch: boolean
) {
  const quotientString = typedValue?.quotient?.toString()

  const contracts = useMemo(() => {
    if (!baultAddress) return []

    const contracts: Array<{
      address: Address
      abi: Abi
      functionName: string
      args?: readonly unknown[]
    }> = [
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'name',
      },
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'symbol',
      },
    ]

    if (shouldFetch && quotientString) {
      contracts.push({
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'previewDeposit',
        args: [BigInt(quotientString)],
      })
    }

    return contracts
  }, [baultAddress, shouldFetch, quotientString])

  const { data, isLoading: isLoadingAll } = useReadContractMultiple({
    contracts,
    query: {
      enabled: !!baultAddress && contracts.length > 0,
    },
  })

  const nameResult = data?.[0]
  const symbolResult = data?.[1]
  const previewResult = data?.[2]

  const isLoading = useMemo(() => {
    const previewLoading = shouldFetch && quotientString ? !previewResult?.result && isLoadingAll : false
    return Boolean(isLoadingAll || previewLoading)
  }, [isLoadingAll, previewResult?.result, shouldFetch, quotientString])

  const vaultName = useMemo(() => {
    return (nameResult?.result as string) || ''
  }, [nameResult?.result])

  const vaultSymbol = useMemo(() => {
    return (symbolResult?.result as string) || ''
  }, [symbolResult?.result])

  const expectedShares = useMemo(() => {
    if (!shouldFetch || !quotientString || !previewResult?.result || !typedValue?.currency) {
      return undefined
    }

    try {
      const formattedShares = formatUnits(previewResult.result as bigint, 18)
      return CurrencyAmount.fromRawAmount(typedValue.currency as Token, parseUnits(formattedShares, 18).toString())
    } catch (error) {
      console.error('Error converting shares:', error)
      return undefined
    }
  }, [previewResult?.result, typedValue?.currency, shouldFetch, quotientString])

  const expectedSharesString = useMemo(() => {
    if (!shouldFetch || !quotientString || !previewResult?.result) {
      return ''
    }
    try {
      return formatUnits(previewResult.result as bigint, 18)
    } catch (error) {
      console.error('Error formatting shares:', error)
      return ''
    }
  }, [previewResult?.result, shouldFetch, quotientString])

  return useMemo(
    () => ({
      expectedShares,
      expectedSharesString,
      isLoading,
      vaultName,
      vaultSymbol,
    }),
    [expectedShares, expectedSharesString, isLoading, vaultName, vaultSymbol]
  )
}
